import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';
import { useValidateShareLink } from '@/features/account/hooks/use-validate-share-link.query';
import { useAuthorization } from '@/auth/hooks/use-authorization';
import { Loader } from '@/components/loader/loader';
import { PageNotFound } from '@/routes/page-not-found';
import { useAccountSwitchOnLinkValidation } from '@/features/account/hooks/use-account-switch-on-link-validation';
import { useCurrentUser } from '@/user/use-current-user.query';
import { SpotlightUnauthenticatedPage } from './spotlight-unauthenticated.page';

export const SpotlightWithLinkWrapperPage = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const { t } = useTranslation('spotlight');
  const { isUserAuthorized } = useAuthorization();
  const currentUser = useCurrentUser();

  const {
    data: validationData,
    error,
    isLoading,
  } = useValidateShareLink(linkId || '');

  const [shouldRedirectAfterSwitch, setShouldRedirectAfterSwitch] =
    useState(false);
  const previousAccountNameRef = useRef<string>('');

  useEffect(() => {
    setShouldRedirectAfterSwitch(false);
    return () => {
      setShouldRedirectAfterSwitch(false);
    };
  }, [linkId]);

  const onSwitchComplete = useCallback((previousAccountName: string) => {
    previousAccountNameRef.current = previousAccountName;
    setShouldRedirectAfterSwitch(true);
  }, []);

  const accountSwitchValidationData =
    validationData?.isValid &&
    validationData.accountId &&
    validationData.accountHasSpotlight !== false
      ? validationData
      : undefined;

  const { handleAccountSwitch, isSwitching, shouldSwitch } =
    useAccountSwitchOnLinkValidation({
      noAccessBehavior: 'redirectToFeed',
      onSwitchComplete,
      validationData: accountSwitchValidationData,
    });

  const spotlightDestination = '/feed?tab=spotlight';

  const getRedirectUrlWithNotification = useCallback(() => {
    if (!validationData) {
      return spotlightDestination;
    }

    const url = new URL(spotlightDestination, window.location.origin);
    url.searchParams.set('accountSwitched', 'true');
    url.searchParams.set('accountName', validationData.accountName || '');
    url.searchParams.set('previousAccountName', previousAccountNameRef.current);
    url.searchParams.set('entityType', 'spotlight');

    return url.pathname + url.search;
  }, [spotlightDestination, validationData]);

  useEffect(() => {
    if (shouldSwitch && !shouldRedirectAfterSwitch) {
      handleAccountSwitch();
    }
  }, [handleAccountSwitch, shouldRedirectAfterSwitch, shouldSwitch]);

  useEffect(() => {
    if (shouldRedirectAfterSwitch && !isSwitching) {
      const redirectUrl = getRedirectUrlWithNotification();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    }
  }, [getRedirectUrlWithNotification, isSwitching, shouldRedirectAfterSwitch]);

  const isWrongPurpose =
    validationData?.isValid && validationData.purpose !== 'SPOTLIGHT';

  const isAuthenticatedWithoutAccess =
    isUserAuthorized &&
    validationData?.isValid &&
    !validationData.accountId &&
    !isLoading &&
    !isSwitching;

  const isAuthenticatedMemberWithoutSpotlight =
    isUserAuthorized &&
    validationData?.isValid &&
    validationData.accountId &&
    validationData.accountHasSpotlight === false &&
    !isLoading &&
    !isSwitching &&
    !shouldRedirectAfterSwitch;

  useEffect(() => {
    if (isAuthenticatedWithoutAccess || isAuthenticatedMemberWithoutSpotlight) {
      window.location.replace(spotlightDestination);
    }
  }, [
    isAuthenticatedMemberWithoutSpotlight,
    isAuthenticatedWithoutAccess,
    spotlightDestination,
  ]);

  const isLoadingState =
    isLoading || isSwitching || (isUserAuthorized && !currentUser.isFetched);

  const isValidRedirect =
    validationData?.isValid &&
    validationData.accountHasSpotlight &&
    validationData.accountId &&
    currentUser.data &&
    currentUser.data.account.id === validationData.accountId;
  const shouldShowNotFound =
    !isLoading &&
    !shouldRedirectAfterSwitch &&
    (error || !validationData?.isValid || isWrongPurpose);

  const loadingComponent = useMemo(
    () => (
      <WithHtmlSavvyTitle title={t`header`}>
        <LoadingContainer>
          <Loader />
        </LoadingContainer>
      </WithHtmlSavvyTitle>
    ),
    [t],
  );

  if (isLoadingState) {
    return loadingComponent;
  }

  if (shouldShowNotFound) {
    return <PageNotFound />;
  }

  if ((isLoading || !validationData) && !shouldRedirectAfterSwitch) {
    return loadingComponent;
  }

  if (shouldRedirectAfterSwitch && !isSwitching) {
    return loadingComponent;
  }

  if (isAuthenticatedWithoutAccess || isAuthenticatedMemberWithoutSpotlight) {
    return loadingComponent;
  }

  if (!validationData || !validationData.accountName) {
    return <PageNotFound />;
  }

  if (!isUserAuthorized) {
    if (!linkId) {
      return <PageNotFound />;
    }

    return (
      <SpotlightUnauthenticatedPage
        linkId={linkId}
        logoUrl={validationData.logoUrl}
        organizationShortName={validationData.shortName}
        organizationName={validationData.accountName}
      />
    );
  }

  if (
    !shouldSwitch &&
    !isSwitching &&
    !shouldRedirectAfterSwitch &&
    isValidRedirect
  ) {
    return <Navigate replace to={spotlightDestination} />;
  }

  return loadingComponent;
};

const LoadingContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;
